/**
 * fieldService_setGeneratorPin: the request/response contract, shared by the callable
 * (functions/src/field-service/pins/setGeneratorPin.ts) and the screens that call it.
 *
 * Plan: docs/superpowers/plans/2026-09-26-generator-location-pinning.md, Task 3.1.
 *
 * WHO MAY CALL: any active employee of the org (not an outside contractor, not an Investor).
 * No module is required (Nick, 2026-09-27).
 *
 * WHAT THE CALLER DOES NOT SEND: the pin's source, who set it and when. The server derives them:
 *   mode "on-site" -> source "tech-pin", confirmedAt = setAt (a person stood at the unit)
 *   mode "map"     -> source "office",   confirmedAt = null  (placed from a desk)
 * A payload carrying any other key (source, setBy, setAt...) is refused as invalid, not silently
 * trimmed. The server also accepts the house-wide optional `organizationId` (must be the caller's
 * own org) and the agent marker `__agentActAs`; screens need send neither.
 *
 * OFFLINE QUEUE (GP1, Nick 2026-09-29). A pin taken with no signal is queued on the phone and sent
 * later. Three optional fields make that safe; a caller that sends none of them behaves as before:
 *   - clientRequestId: a replay by the SAME person of the same id (or of the same exact capturedAt)
 *     on the same generator writes nothing and returns the original outcome (`replayed: true`).
 *     Another person's id never replays. On-site pins and confirms only.
 *   - A queued call MUST say when it was taken: clientRequestId requires capturedAt (set), and
 *     capturedAt + pinSetAtSeen (confirm). Otherwise invalid-argument: a queued call must never
 *     silently fall back to "apply now".
 *   - capturedAt (on-site set and confirm): when the fix was taken. RECORDED as the pin's capturedAt
 *     (and confirmFix.capturedAt on a confirm); setAt and confirmedAt stay the server's receipt time.
 *     More than CAPTURED_AT_MAX_FUTURE_MS ahead of the server (a wrong phone clock) is CLAMPED to the
 *     receipt time and marked capturedAtClamped, not refused: the tech really stood at the unit, and
 *     the receipt time is a true upper bound on when. Older than QUEUED_PIN_MAX_AGE_DAYS at receipt
 *     (checked after replay detection) is the outcome `expired`.
 *     A queued pin never overwrites a tech-pin taken after it (outcome `superseded`); "taken" is the
 *     capture time on both sides (capturedAt, else setAt), whether that pin came from a set or a confirm.
 *   - pinSetAtSeen (confirm only): the setAt of the pin the tech was looking at. If the pin has
 *     changed since, nothing is written (outcome `pin-changed`).
 *
 * Pure types and constants only: no Firestore, no zod, no runtime dependencies.
 */

/** How the pin was placed. */
export type GeneratorPinMode = 'on-site' | 'map';
export const GENERATOR_PIN_MODES: readonly GeneratorPinMode[] = ['on-site', 'map'];

/**
 * Why an existing pin is being replaced. Optional; recorded on the REPLACED pin's history entry.
 * - wrong-location: the old point was never the unit. The old point is KEPT in history, marked
 *   with this reason, so pins can be audited and a mistaken replacement undone (Nick, 2026-09-27).
 * - unit-moved: the unit was physically relocated; the old point is kept in history.
 * - more-precise: same unit, same place, a better point; the old point is kept in history.
 */
export type GeneratorPinReason = 'wrong-location' | 'unit-moved' | 'more-precise';
export const GENERATOR_PIN_REASONS: readonly GeneratorPinReason[] = ['wrong-location', 'unit-moved', 'more-precise'];

/** Precedence, highest first: tech-pin > office > photo-exif > photo-derived. */
export type GeneratorPinSource = 'tech-pin' | 'office' | 'photo-exif' | 'photo-derived';

interface SetGeneratorPinBase {
  /** appsheet_generators document id: `${appId}_${Generator ID}`, not the bare Generator ID. */
  generatorDocId: string;
  /** WGS84 degrees, [-90, 90]. */
  lat: number;
  /** WGS84 degrees, [-180, 180]. */
  lng: number;
  reason?: GeneratorPinReason;
}

/**
 * An idempotency key minted on the phone when the action is queued: 8-128 chars of [A-Za-z0-9_-]
 * (a UUID fits). Scoped to the generator.
 */
export const CLIENT_REQUEST_ID_PATTERN = /^[A-Za-z0-9_-]{8,128}$/;
/** How many processed request ids a generator remembers (newest kept). */
export const PIN_REQUEST_ID_CAP = 20;
/** Clock skew tolerated on capturedAt: 2 minutes ahead of the server. */
export const CAPTURED_AT_MAX_FUTURE_MS = 2 * 60_000;
/** The phone's queue cap: a queued pin older than this is `expired`, not saved. */
export const QUEUED_PIN_MAX_AGE_DAYS = 30;

/** A tech standing at the unit, using the phone's live fix. */
export interface SetGeneratorPinOnSiteRequest extends SetGeneratorPinBase {
  mode: 'on-site';
  /** The device's reported accuracy in metres, (0, 5000]. Required. */
  accuracy: number;
  /** Offline queue: idempotency key (see CLIENT_REQUEST_ID_PATTERN). */
  clientRequestId?: string;
  /** Offline queue: ISO-8601 time the fix was taken. Recorded, never used as setAt. */
  capturedAt?: string;
}

/** Placed on a map by hand. There is no device fix, so no accuracy: omit it or send null. */
export interface SetGeneratorPinMapRequest extends SetGeneratorPinBase {
  mode: 'map';
  accuracy?: null;
}

export type SetGeneratorPinRequest = SetGeneratorPinOnSiteRequest | SetGeneratorPinMapRequest;

/** Who set a pin. Server-derived from the caller's own profile. */
export interface GeneratorPinSetByDto {
  uid: string;
  displayName: string;
}

/** A replaced pin, point included whatever the reason. Nullable only to tolerate a malformed entry. */
export interface GeneratorPinHistoryEntryDto {
  lat: number | null;
  lng: number | null;
  accuracy: number | null;
  source: GeneratorPinSource;
  setBy: GeneratorPinSetByDto;
  /** ISO-8601. */
  setAt: string | null;
  replacedReason?: string;
}

/**
 * The saved pin as the callable RETURNS it. Timestamps are ISO-8601 strings here because a
 * callable response cannot carry a Firestore Timestamp. On the generator document itself
 * (`_opgUnit.pin`) the same fields are Firestore Timestamps.
 */
export interface GeneratorPinDto {
  lat: number;
  lng: number;
  /** Metres; null for a map-placed pin. */
  accuracy: number | null;
  source: GeneratorPinSource;
  setBy: GeneratorPinSetByDto;
  /** When the SERVER received and saved the pin. */
  setAt: string;
  /** Queued on-site pins and queued confirms only: when the phone took the fix (client-reported). */
  capturedAt?: string;
  /** True when the phone's clock was ahead and capturedAt was clamped to the receipt time. */
  capturedAtClamped?: boolean;
  confirmedAt: string | null;
  /**
   * Only on a tech-pin made by "Yes, it's here": the phone's reading at the confirm and its distance
   * from the pin, kept whether or not the pin moved.
   */
  confirmFix?: { lat: number; lng: number; accuracy: number; distanceM: number; capturedAt?: string };
  /** Oldest first, newest last; capped server-side (oldest dropped). */
  history: GeneratorPinHistoryEntryDto[];
}

/**
 * Every outcome is a normal return, not an error. Errors (thrown HttpsError) are reserved for:
 * unauthenticated, permission-denied (not an active employee, or another org's unit),
 * not-found (no such generator), invalid-argument (bad payload), resource-exhausted (rate limit).
 */
export type SetGeneratorPinResponse =
  /** replayed: true when this clientRequestId was already processed; nothing new was written. */
  | { outcome: 'saved'; pin: GeneratorPinDto; replayed?: true }
  /** On-site only: the phone's fix is too vague to pin with. Nothing was written. */
  | { outcome: 'fix-too-weak'; accuracy: number; maxAccuracyM: number }
  /** The point is outside the service territory (a typo'd or null-island point). Nothing was written. */
  | { outcome: 'out-of-territory' }
  /** Queued on-site pin: the unit already has a tech-pin taken after this one. Nothing was written. */
  | { outcome: 'superseded'; currentPin: GeneratorPinDto }
  /** Queued on-site pin older than QUEUED_PIN_MAX_AGE_DAYS. Nothing was written; drop it from the queue. */
  | { outcome: 'expired'; maxAgeDays: number };

export type SetGeneratorPinOutcome = SetGeneratorPinResponse['outcome'];

/** The callable's deployed name. */
export const SET_GENERATOR_PIN_CALLABLE = 'fieldService_setGeneratorPin';

// ---------------------------------------------------------------------------------------------
// "Yes, it's here": a tech standing at the unit confirms its pin.
// Plan: docs/superpowers/plans/2026-09-26-generator-location-pinning.md, Task 3.2 + lane DoD.
// ---------------------------------------------------------------------------------------------

/**
 * The tech's LIVE fix. The pin becomes source "tech-pin" with confirmedAt stamped. It STAYS at its
 * existing point unless the fix is very precise AND clearly different (Nick, 2026-09-28): accuracy
 * <= 20 m and distance from the pin > that accuracy (a confirm farther than 75 m is refused as
 * too-far). Then it moves to the fix (`moved: true`). A vague fix never drags a precise pin to a worse
 * spot, and a precise fix that agrees with the pin leaves it alone.
 * Confirmable sources: office, photo-exif, photo-derived. A tech-pin is already confirmed.
 */
export interface ConfirmGeneratorPinRequest {
  generatorDocId: string;
  lat: number;
  lng: number;
  /** The device's reported accuracy in metres, (0, 5000]. */
  accuracy: number;
  /** Offline queue: idempotency key (see CLIENT_REQUEST_ID_PATTERN). */
  clientRequestId?: string;
  /** ISO-8601 setAt of the pin the tech was confirming. If the pin changed since: `pin-changed`. */
  pinSetAtSeen?: string;
  /** Offline queue: ISO-8601 time the reading was taken. Recorded, never used as confirmedAt. */
  capturedAt?: string;
}

export type ConfirmGeneratorPinResponse =
  /**
   * moved: true when the pin was moved to the live fix; false when it was confirmed where it was.
   * replayed: true when this clientRequestId was already processed; nothing new was written.
   */
  | { outcome: 'confirmed'; pin: GeneratorPinDto; moved: boolean; replayed?: true }
  /** pinSetAtSeen no longer matches the unit's pin: someone changed it. Nothing was written. */
  | { outcome: 'pin-changed' }
  /** Queued confirm older than QUEUED_PIN_MAX_AGE_DAYS. Nothing was written; drop it from the queue. */
  | { outcome: 'expired'; maxAgeDays: number }
  /** The phone's fix is too vague. Nothing was written. */
  | { outcome: 'fix-too-weak'; accuracy: number; maxAccuracyM: number }
  /** The tech is too far from the pin to vouch for it; offer "Update pin" instead. Nothing was written. */
  | { outcome: 'too-far'; distanceM: number; maxDistanceM: number }
  /** No pin, or it is already a tech-pin. Nothing was written. */
  | { outcome: 'nothing-to-confirm' };

export const CONFIRM_GENERATOR_PIN_CALLABLE = 'fieldService_confirmGeneratorPin';

/** History reasons written by the server (not choosable by a person, unlike GeneratorPinReason). */
export const PIN_REPLACED_CONFIRMED_ON_SITE = 'confirmed-on-site';
export const PIN_REPLACED_SUGGESTION_ACCEPTED = 'suggestion-accepted';

// ---------------------------------------------------------------------------------------------
// Pin suggestions: candidate locations from photo EXIF, decided by a person.
// Stored at `_opgUnit.pinSuggestions` (array, capped); the count of pending ones at
// `_opgUnit.pinSuggestionsPending`. Written by the photo backfill / sweep (server-side
// `addPinSuggestion`), decided through fieldService_reviewPinSuggestion.
// ---------------------------------------------------------------------------------------------

export type PinSuggestionStatus = 'pending' | 'accepted' | 'dismissed';

/** Enough to audit a photo-derived candidate without re-running the derivation. */
export interface PinSuggestionEvidenceDto {
  photoCount: number;
  visitCount: number;
  spreadMeters: number;
  distanceFromAddressMeters: number | null;
  /** Up to 5 photo references (Storage paths or goformz_form_images ids). */
  samplePhotoRefs: string[];
  linkRoutes: string[];
  derivationVersion: string;
  /** Local days (YYYY-MM-DD, sorted) the cluster's photos were taken; [] for evidence written before. */
  visitDays: string[];
  /**
   * Each trip's photo day beside the day its report was filed (sorted by photo day, newest 20);
   * [] for evidence written before. A photo day far from its report day is an old photo or a picture
   * of a picture.
   */
  visitReports: PinSuggestionVisitReportDto[];
}

/** One trip: when its photos were taken, and when its report was filed (null when unknown). */
export interface PinSuggestionVisitReportDto {
  /** Local YYYY-MM-DD. */
  photoDay: string;
  /** Local YYYY-MM-DD, or null when the report's date could not be read. */
  reportDay: string | null;
}

export interface PinSuggestionDto {
  /** Deterministic: the same evidence under the same derivationVersion always has the same id. */
  id: string;
  /**
   * Null when the point was withheld (it failed the address check, or the unit has no address to
   * check against). Such a suggestion is shown as a warning and cannot be accepted.
   */
  lat: number | null;
  lng: number | null;
  evidence: PinSuggestionEvidenceDto;
  /** Why it was a suggestion rather than a pin (the derivation's failed gates). */
  gateFailures: string[];
  /** ISO-8601. */
  suggestedAt: string;
  status: PinSuggestionStatus;
  /** Set once decided. */
  decidedBy?: GeneratorPinSetByDto;
  decidedAt?: string;
}

export type PinSuggestionDecision = 'accept' | 'dismiss';

/**
 * Gate failures that make fieldService_reviewPinSuggestion REFUSE an accept: the photos were taken at
 * or near a branch yard, so the point is where the unit was worked on, not where it lives.
 * Other gate failures are warnings shown to the reviewer. The one definition, used by server and UI.
 */
export const ACCEPT_REFUSED_PIN_SUGGESTION_GATES: readonly string[] = ['near-depot', 'depot-mismatch', 'depot-unresolved'];

export interface ReviewPinSuggestionRequest {
  generatorDocId: string;
  suggestionId: string;
  decision: PinSuggestionDecision;
}

export type ReviewPinSuggestionResponse =
  /** Accepted: the pin is now this suggestion's point, source "photo-exif", confirmedAt stamped. */
  | { outcome: 'accepted'; pin: GeneratorPinDto }
  | { outcome: 'dismissed' }
  /** The unit already has a pin set by a person standing at it; dismiss instead. Nothing was written. */
  | { outcome: 'has-confirmed-pin' }
  /** The suggestion has no point (withheld); it can only be dismissed. Nothing was written. */
  | { outcome: 'no-point' }
  /**
   * Refused on its evidence: the point is near a branch yard (photos taken at the shop, not the
   * site), or outside the service territory. Dismiss instead. Nothing was written.
   */
  | { outcome: 'refused'; reasons: string[] }
  /** Someone already decided it (or it no longer exists). Nothing was written. */
  | { outcome: 'already-decided'; status: PinSuggestionStatus | 'missing' };

export const REVIEW_PIN_SUGGESTION_CALLABLE = 'fieldService_reviewPinSuggestion';

/** The review queue: units with at least one pending suggestion. */
export interface ListPinSuggestionsRequest {
  /** Default 50, max 200. */
  limit?: number;
}

export interface PinSuggestionQueueItemDto {
  generatorDocId: string;
  generatorId: string;
  clientName: string;
  address: string;
  /** The unit's current pin, if any, so a reviewer can compare. */
  currentPin: GeneratorPinDto | null;
  /** Pending suggestions only. */
  suggestions: PinSuggestionDto[];
}

export interface ListPinSuggestionsResponse {
  items: PinSuggestionQueueItemDto[];
  /** True when more units MAY have pending suggestions than were returned (never false while more exist). */
  truncated: boolean;
}

export const LIST_PIN_SUGGESTIONS_CALLABLE = 'fieldService_listPinSuggestions';
